/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

export type PayrollStatus = 'draft' | 'processed' | 'paid';

export interface PayrollRun {
  readonly id: string;
  readonly periodLabel: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: PayrollStatus;
  readonly totalPaise: number;
  readonly notes: string | null;
  readonly processedAt: string | null;
  readonly createdAt: string;
}

export interface Payslip {
  readonly id: string;
  readonly runId: string;
  readonly therapistId: string;
  readonly therapistName: string;
  readonly sessionsCount: number;
  readonly grossPaise: number;
  readonly notes: string | null;
}

export interface PayrollRunDetail extends PayrollRun {
  readonly payslips: readonly Payslip[];
}

/** One therapist on the Figma Payroll roster (per-therapist, current month). */
export interface PayrollRosterRow {
  readonly therapistId: string;
  readonly therapistName: string;
  readonly category: 'on_roll' | 'consultant';
  readonly specialisation: string | null;
  readonly monthlyFixedPaise: number | null;
  readonly perSessionRatePaise: number | null;
  readonly sessionsCount: number;
  readonly status: 'paid' | 'pending';
}

export interface CreateRunPayload {
  readonly periodLabel: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly notes?: string;
}

export interface AddPayslipPayload {
  readonly therapistId: string;
  readonly grossPaise: number;
  readonly sessionsCount?: number;
  readonly notes?: string;
}

const BASE = '/finance/payroll/runs';

export const payrollApi = {
  async roster(): Promise<readonly PayrollRosterRow[]> {
    const { data } = await apiClient.get<readonly PayrollRosterRow[]>('/finance/payroll/roster');
    return data;
  },
  async listRuns(): Promise<readonly PayrollRun[]> {
    const { data } = await apiClient.get<readonly PayrollRun[]>(BASE);
    return data;
  },
  async getRun(id: string): Promise<PayrollRunDetail> {
    const { data } = await apiClient.get<PayrollRunDetail>(`${BASE}/${id}`);
    return data;
  },
  async createRun(payload: CreateRunPayload): Promise<PayrollRun> {
    const { data } = await apiClient.post<PayrollRun>(BASE, payload);
    return data;
  },
  async addPayslip(id: string, payload: AddPayslipPayload): Promise<PayrollRun> {
    const { data } = await apiClient.post<PayrollRun>(`${BASE}/${id}/payslips`, payload);
    return data;
  },
  async process(id: string): Promise<PayrollRun> {
    const { data } = await apiClient.post<PayrollRun>(`${BASE}/${id}/process`);
    return data;
  },
};
